import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Pencil, Trash2, X, GripVertical } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import ConfirmationModal from '@/components/ConfirmationModal';
import { API } from '@/App';
import { toast } from 'sonner';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const MultipleChoice = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get tab from URL, default to 'finish-types'
  const [activeTab, setActiveTab] = useState(
    searchParams.get('tab') || 'finish-types'
  );
  
  const [cwFinishTypes, setCwFinishTypes] = useState([]);
  const [windowOperationTypes, setWindowOperationTypes] = useState([]);
  const [sfMaterials, setSfMaterials] = useState([]);
  const [glassTypeSpecs, setGlassTypeSpecs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [newItemName, setNewItemName] = useState('');

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle tab change and update URL
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  useEffect(() => {
    if (activeTab === 'finish-types') {
      loadCwFinishTypes();
    } else if (activeTab === 'window-operation-types') {
      loadWindowOperationTypes();
    } else if (activeTab === 'materials') {
      loadSfMaterials();
    } else if (activeTab === 'glass-type-specs') {
      loadGlassTypeSpecs();
    }
  }, [activeTab]);

  const loadCwFinishTypes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/cw-finish-types`);
      setCwFinishTypes(response.data || []);
    } catch (error) {
      console.error('Error loading CW Finish Types:', error);
      toast.error('Failed to load CW Finish Types');
    } finally {
      setLoading(false);
    }
  };

  const loadWindowOperationTypes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/window-operation-types`);
      setWindowOperationTypes(response.data || []);
    } catch (error) {
      console.error('Error loading Window Operation Types:', error);
      toast.error('Failed to load Window Operation Types');
    } finally {
      setLoading(false);
    }
  };

  const loadSfMaterials = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/sf-materials`);
      setSfMaterials(response.data || []);
    } catch (error) {
      console.error('Error loading SF Materials:', error);
      toast.error('Failed to load SF Materials');
    } finally {
      setLoading(false);
    }
  };

  const loadGlassTypeSpecs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/glass-type-specs`);
      setGlassTypeSpecs(response.data || []);
    } catch (error) {
      console.error('Error loading Glass Type Specs:', error);
      toast.error('Failed to load Glass Type Specs');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newItemName.trim()) {
      toast.error('Please enter a name');
      return;
    }

    try {
      let response, endpoint, successMsg, errorMsg;
      
      if (activeTab === 'finish-types') {
        endpoint = `${API}/cw-finish-types`;
        successMsg = 'Finish Type added successfully';
      } else if (activeTab === 'window-operation-types') {
        endpoint = `${API}/window-operation-types`;
        successMsg = 'Window Operation Type added successfully';
      } else if (activeTab === 'materials') {
        endpoint = `${API}/sf-materials`;
        successMsg = 'Material added successfully';
      } else if (activeTab === 'glass-type-specs') {
        endpoint = `${API}/glass-type-specs`;
        successMsg = 'Glass Type Spec added successfully';
      }
      
      response = await axios.post(endpoint, { name: newItemName.trim() });
      
      if (activeTab === 'finish-types') {
        setCwFinishTypes([...cwFinishTypes, response.data]);
      } else if (activeTab === 'window-operation-types') {
        setWindowOperationTypes([...windowOperationTypes, response.data]);
      } else if (activeTab === 'materials') {
        setSfMaterials([...sfMaterials, response.data]);
      } else if (activeTab === 'glass-type-specs') {
        setGlassTypeSpecs([...glassTypeSpecs, response.data]);
      }
      
      toast.success(successMsg);
      setShowAddModal(false);
      setNewItemName('');
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error(error.response?.data?.detail || 'Failed to add item');
    }
  };

  const handleEdit = async () => {
    if (!newItemName.trim()) {
      toast.error('Please enter a name');
      return;
    }

    try {
      let response, endpoint;
      
      if (activeTab === 'finish-types') {
        endpoint = `${API}/cw-finish-types/${editingItem.id}`;
      } else if (activeTab === 'window-operation-types') {
        endpoint = `${API}/window-operation-types/${editingItem.id}`;
      } else if (activeTab === 'materials') {
        endpoint = `${API}/sf-materials/${editingItem.id}`;
      } else if (activeTab === 'glass-type-specs') {
        endpoint = `${API}/glass-type-specs/${editingItem.id}`;
      }
      
      response = await axios.put(endpoint, { name: newItemName.trim() });
      
      if (activeTab === 'finish-types') {
        setCwFinishTypes(cwFinishTypes.map(item => 
          item.id === editingItem.id ? response.data : item
        ));
      } else if (activeTab === 'window-operation-types') {
        setWindowOperationTypes(windowOperationTypes.map(item => 
          item.id === editingItem.id ? response.data : item
        ));
      } else if (activeTab === 'materials') {
        setSfMaterials(sfMaterials.map(item => 
          item.id === editingItem.id ? response.data : item
        ));
      } else if (activeTab === 'glass-type-specs') {
        setGlassTypeSpecs(glassTypeSpecs.map(item => 
          item.id === editingItem.id ? response.data : item
        ));
      }
      
      toast.success('Item updated successfully');
      setShowEditModal(false);
      setEditingItem(null);
      setNewItemName('');
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error(error.response?.data?.detail || 'Failed to update item');
    }
  };

  const openDeleteModal = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      let endpoint;
      
      if (activeTab === 'finish-types') {
        endpoint = `${API}/cw-finish-types/${itemToDelete.id}`;
      } else if (activeTab === 'window-operation-types') {
        endpoint = `${API}/window-operation-types/${itemToDelete.id}`;
      } else if (activeTab === 'materials') {
        endpoint = `${API}/sf-materials/${itemToDelete.id}`;
      } else if (activeTab === 'glass-type-specs') {
        endpoint = `${API}/glass-type-specs/${itemToDelete.id}`;
      }
      
      await axios.delete(endpoint);
      
      if (activeTab === 'finish-types') {
        setCwFinishTypes(cwFinishTypes.filter(item => item.id !== itemToDelete.id));
      } else if (activeTab === 'window-operation-types') {
        setWindowOperationTypes(windowOperationTypes.filter(item => item.id !== itemToDelete.id));
      } else if (activeTab === 'materials') {
        setSfMaterials(sfMaterials.filter(item => item.id !== itemToDelete.id));
      } else if (activeTab === 'glass-type-specs') {
        setGlassTypeSpecs(glassTypeSpecs.filter(item => item.id !== itemToDelete.id));
      }
      
      toast.success('Item deleted successfully');
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete item');
    } finally {
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setNewItemName(item.name);
    setShowEditModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setNewItemName('');
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingItem(null);
    setNewItemName('');
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    let currentItems, setCurrentItems, endpoint, reloadFunc;
    
    if (activeTab === 'finish-types') {
      currentItems = cwFinishTypes;
      setCurrentItems = setCwFinishTypes;
      endpoint = `${API}/cw-finish-types/reorder`;
      reloadFunc = loadCwFinishTypes;
    } else if (activeTab === 'window-operation-types') {
      currentItems = windowOperationTypes;
      setCurrentItems = setWindowOperationTypes;
      endpoint = `${API}/window-operation-types/reorder`;
      reloadFunc = loadWindowOperationTypes;
    } else if (activeTab === 'materials') {
      currentItems = sfMaterials;
      setCurrentItems = setSfMaterials;
      endpoint = `${API}/sf-materials/reorder`;
      reloadFunc = loadSfMaterials;
    } else if (activeTab === 'glass-type-specs') {
      currentItems = glassTypeSpecs;
      setCurrentItems = setGlassTypeSpecs;
      endpoint = `${API}/glass-type-specs/reorder`;
      reloadFunc = loadGlassTypeSpecs;
    }

    const oldIndex = currentItems.findIndex(item => item.id === active.id);
    const newIndex = currentItems.findIndex(item => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Reorder array
    const reorderedItems = arrayMove(currentItems, oldIndex, newIndex);

    // Update order field for all items
    const updatedItems = reorderedItems.map((item, index) => ({
      id: item.id,
      order: index
    }));

    // OPTIMISTIC UPDATE: Update frontend state immediately
    const newItems = reorderedItems.map((item, index) => ({
      ...item,
      order: index
    }));
    
    setCurrentItems(newItems);

    // Send to backend in background
    try {
      await axios.put(endpoint, { items: updatedItems });
    } catch (error) {
      console.error('Error reordering items:', error);
      toast.error('Failed to reorder items');
      reloadFunc();
    }
  };

  const tabs = [
    { id: 'finish-types', label: 'Finish Types' },
    { id: 'window-operation-types', label: 'Window Operation Types' },
    { id: 'materials', label: 'Materials' },
    { id: 'glass-type-specs', label: 'Glass Type Specs' },
  ];

  // Sortable Item Component
  const SortableItem = ({ item, onEdit, onDelete }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: item.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
      >
        {/* Drag Handle */}
        <div {...attributes} {...listeners} className="cursor-move mr-3 text-gray-400 hover:text-gray-600">
          <GripVertical className="w-5 h-5" />
        </div>
        
        <span className="flex-1 text-gray-800 font-medium">{item.name}</span>
        
        <div className="flex gap-2">
          <button
            onClick={() => onEdit(item)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(item)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold mb-2" style={{ background: '#FF6600', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Multiple Choice
          </h2>
          <p className="text-gray-600 mb-8">Manage multiple choice options for different categories</p>

          {/* Tabs */}
          <div className="bg-white rounded-lg shadow-sm mb-6">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6" aria-label="Tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`
                      py-4 px-1 border-b-2 font-medium text-sm transition-colors
                      ${activeTab === tab.id
                        ? 'border-orange-500 text-orange-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Content Area */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            {/* Header with Add Button */}
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-800">
                {activeTab === 'finish-types' && 'Finish Types'}
                {activeTab === 'window-operation-types' && 'Window Operation Types'}
                {activeTab === 'materials' && 'Materials'}
                {activeTab === 'glass-type-specs' && 'Glass Type Specs'}
              </h3>
              <Button
                onClick={() => setShowAddModal(true)}
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add New
              </Button>
            </div>

            {/* List */}
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : (
              <>
                {activeTab === 'finish-types' && (
                  <div className="space-y-2">
                    {cwFinishTypes.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No Finish Types found. Click "Add New" to create one.
                      </div>
                    ) : (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={cwFinishTypes.map(item => item.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          {cwFinishTypes.map((item) => (
                            <SortableItem
                              key={item.id}
                              item={item}
                              onEdit={openEditModal}
                              onDelete={openDeleteModal}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>
                )}

                {activeTab === 'window-operation-types' && (
                  <div className="space-y-2">
                    {windowOperationTypes.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No Window Operation Types found. Click "Add New" to create one.
                      </div>
                    ) : (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={windowOperationTypes.map(item => item.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          {windowOperationTypes.map((item) => (
                            <SortableItem
                              key={item.id}
                              item={item}
                              onEdit={openEditModal}
                              onDelete={openDeleteModal}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>
                )}

                {activeTab === 'materials' && (
                  <div className="space-y-2">
                    {sfMaterials.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No Materials found. Click "Add New" to create one.
                      </div>
                    ) : (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={sfMaterials.map(item => item.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          {sfMaterials.map((item) => (
                            <SortableItem
                              key={item.id}
                              item={item}
                              onEdit={openEditModal}
                              onDelete={openDeleteModal}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>
                )}

                {activeTab === 'glass-type-specs' && (
                  <div className="space-y-2">
                    {glassTypeSpecs.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No Glass Type Specs found. Click "Add New" to create one.
                      </div>
                    ) : (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={glassTypeSpecs.map(item => item.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          {glassTypeSpecs.map((item) => (
                            <SortableItem
                              key={item.id}
                              item={item}
                              onEdit={openEditModal}
                              onDelete={openDeleteModal}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Add Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {activeTab === 'finish-types' && 'Add Finish Type'}
              {activeTab === 'window-operation-types' && 'Add Window Operation Type'}
              {activeTab === 'materials' && 'Add Material'}
              {activeTab === 'glass-type-specs' && 'Add Glass Type Spec'}
            </DialogTitle>
            <DialogDescription>
              {activeTab === 'finish-types' && 'Enter the name for the new Finish Type'}
              {activeTab === 'window-operation-types' && 'Enter the name for the new Window Operation Type'}
              {activeTab === 'materials' && 'Enter the name for the new Material'}
              {activeTab === 'glass-type-specs' && 'Enter the name for the new Glass Type Spec'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="Enter name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAdd();
                  }
                }}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={closeAddModal}>
              Cancel
            </Button>
            <Button onClick={handleAdd} className="bg-orange-600 hover:bg-orange-700">
              Add
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {activeTab === 'finish-types' && 'Edit Finish Type'}
              {activeTab === 'window-operation-types' && 'Edit Window Operation Type'}
              {activeTab === 'materials' && 'Edit Material'}
              {activeTab === 'glass-type-specs' && 'Edit Glass Type Spec'}
            </DialogTitle>
            <DialogDescription>
              {activeTab === 'finish-types' && 'Update the name for this Finish Type'}
              {activeTab === 'window-operation-types' && 'Update the name for this Window Operation Type'}
              {activeTab === 'materials' && 'Update the name for this Material'}
              {activeTab === 'glass-type-specs' && 'Update the name for this Glass Type Spec'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="Enter name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleEdit();
                  }
                }}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={closeEditModal}>
              Cancel
            </Button>
            <Button onClick={handleEdit} className="bg-orange-600 hover:bg-orange-700">
              Update
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDelete}
        title={
          activeTab === 'finish-types' ? 'Delete Finish Type' : 
          activeTab === 'window-operation-types' ? 'Delete Window Operation Type' : 
          activeTab === 'materials' ? 'Delete Material' :
          'Delete Glass Type Spec'
        }
        message={`Are you sure you want to delete "${itemToDelete?.name}"? This action cannot be undone.`}
        type="danger"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </AdminLayout>
  );
};

export default MultipleChoice;
